import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert,
  AlertTitle,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import {
  CloudUpload as UploadIcon,
  Description as FileIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
  GitHub as GitHubIcon,
  Link as LinkIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import candidateTheme from '../../theme/candidateTheme';
import api from '../../api';
import { showSuccess, showError } from '../../utils/sweetAlertConfig';
import CandidateHeader from '../../components/CandidateHeader';

const PRIMARY = "#29204C";
const SUCCESS = "#68B977";
const WARNING = "#f39c12";
const INFO = "#3498db";

const TechnicalTaskSubmission = () => {
  const { assignment_id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [task, setTask] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [files, setFiles] = useState([]);
  const [notes, setNotes] = useState('');
  const [githubUrl, setGithubUrl] = useState('');
  const [liveDemoUrl, setLiveDemoUrl] = useState('');

  useEffect(() => {
    loadTaskDetails();
  }, [assignment_id]);

  const loadTaskDetails = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/candidate/technical-task/${assignment_id}`);
      setTask(response.data.task);
      setSubmissions(response.data.submissions);
    } catch (error) {
      showError('Failed to load task details');
      navigate('/candidate/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files);

    // Check file count
    if (files.length + selectedFiles.length > task.max_files) {
      showError(`Maximum ${task.max_files} files allowed`);
      return;
    }

    // Check file size
    for (const file of selectedFiles) {
      if (file.size > task.max_file_size * 1024 * 1024) {
        showError(`File ${file.name} exceeds maximum size of ${task.max_file_size}MB`);
        return;
      }

      // Check file type
      const extension = file.name.split('.').pop().toLowerCase();
      if (!task.allowed_file_types.includes(extension)) {
        showError(`File type .${extension} is not allowed. Allowed types: ${task.allowed_file_types.join(', ')}`);
        return;
      }
    }

    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const handleRemoveFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (files.length === 0) {
      showError('Please upload at least one file');
      return;
    }

    setSubmitting(true);
    try {
      const candidateData = JSON.parse(localStorage.getItem('candidate_data') || '{}');

      const formData = new FormData();
      formData.append('assignment_id', assignment_id);
      formData.append('candidate_id', candidateData.candidate_id);
      formData.append('notes', notes);
      formData.append('github_url', githubUrl);
      formData.append('live_demo_url', liveDemoUrl);

      files.forEach((file, index) => {
        formData.append(`files[${index}]`, file);
      });

      await api.post('/candidate/technical-task/submit', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      showSuccess('Task submitted successfully!', 'Your submission is now under review.');
      navigate('/candidate/dashboard');
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to submit task');
    } finally {
      setSubmitting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const getDaysRemaining = () => {
    const deadline = new Date(task.deadline_date);
    const now = new Date();
    const diff = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  if (loading) {
    return (
      <ThemeProvider theme={candidateTheme}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  if (!task) {
    return (
      <ThemeProvider theme={candidateTheme}>
        <Container>
          <Alert severity="error">Task not found</Alert>
        </Container>
      </ThemeProvider>
    );
  }

  const daysRemaining = getDaysRemaining();
  const hasSubmission = submissions.length > 0;
  const latestSubmission = submissions[0];

  return (
    <ThemeProvider theme={candidateTheme}>
      <CandidateHeader />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Task Header */}
        <Paper sx={{ p: 3, mb: 3, background: `linear-gradient(135deg, ${PRIMARY} 0%, #1a1635 100%)`, color: 'white' }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {task.title}
          </Typography>
          <Box display="flex" gap={2} flexWrap="wrap" mt={2}>
            <Chip
              icon={<ScheduleIcon />}
              label={`Deadline: ${new Date(task.deadline_date).toLocaleDateString()}`}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
            <Chip
              icon={<ScheduleIcon />}
              label={`${daysRemaining} days remaining`}
              sx={{
                bgcolor: daysRemaining > 3 ? SUCCESS : WARNING,
                color: 'white',
                fontWeight: 600,
              }}
            />
            <Chip
              label={`Max ${task.max_files} files`}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
            <Chip
              label={`Max ${task.max_file_size}MB per file`}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
          </Box>
        </Paper>

        {/* Existing Submission Alert */}
        {hasSubmission && (
          <Alert
            severity={latestSubmission.review_status === 'reviewed' ? 'success' : 'info'}
            icon={latestSubmission.review_status === 'reviewed' ? <CheckIcon /> : <ScheduleIcon />}
            sx={{ mb: 3 }}
          >
            <AlertTitle>
              {latestSubmission.review_status === 'reviewed' ? 'Task Reviewed' : 'Submission Under Review'}
            </AlertTitle>
            {latestSubmission.review_status === 'reviewed' ? (
              <>
                <Typography variant="body2">
                  <strong>Score:</strong> {latestSubmission.score}%
                </Typography>
                {latestSubmission.feedback && (
                  <Typography variant="body2" mt={1}>
                    <strong>Feedback:</strong> {latestSubmission.feedback}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body2">
                Your submission is being reviewed. You'll be notified once the review is complete.
              </Typography>
            )}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Task Details */}
          <Grid item xs={12} md={7}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Task Description
                </Typography>
                <Typography variant="body1" paragraph whiteSpace="pre-line">
                  {task.description}
                </Typography>

                {task.requirements && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Requirements
                    </Typography>
                    <Typography variant="body1" paragraph whiteSpace="pre-line">
                      {task.requirements}
                    </Typography>
                  </>
                )}

                {task.evaluation_criteria && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Evaluation Criteria
                    </Typography>
                    <Typography variant="body1" whiteSpace="pre-line">
                      {task.evaluation_criteria}
                    </Typography>
                  </>
                )}
              </CardContent>
            </Card>

            {/* File Upload Section */}
            {!hasSubmission && (
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Upload Your Work
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <input
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                      id="file-upload"
                      accept={task.allowed_file_types.map(type => `.${type}`).join(',')}
                    />
                    <label htmlFor="file-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        startIcon={<UploadIcon />}
                        fullWidth
                        sx={{ py: 2, borderStyle: 'dashed' }}
                      >
                        Choose Files to Upload
                      </Button>
                    </label>
                    <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                      Allowed types: {task.allowed_file_types.join(', ')} | Max {task.max_files} files | Max {task.max_file_size}MB per file
                    </Typography>
                  </Box>

                  {/* Selected Files List */}
                  {files.length > 0 && (
                    <List>
                      {files.map((file, index) => (
                        <ListItem
                          key={index}
                          secondaryAction={
                            <IconButton edge="end" onClick={() => handleRemoveFile(index)}>
                              <DeleteIcon />
                            </IconButton>
                          }
                        >
                          <ListItemIcon>
                            <FileIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={file.name}
                            secondary={formatFileSize(file.size)}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}

                  <Divider sx={{ my: 3 }} />

                  {/* Optional URLs */}
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="GitHub Repository URL (Optional)"
                        value={githubUrl}
                        onChange={(e) => setGithubUrl(e.target.value)}
                        placeholder="https://github.com/username/repo"
                        InputProps={{
                          startAdornment: <GitHubIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Live Demo URL (Optional)"
                        value={liveDemoUrl}
                        onChange={(e) => setLiveDemoUrl(e.target.value)}
                        placeholder="https://your-demo-site.com"
                        InputProps={{
                          startAdornment: <LinkIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Additional Notes (Optional)"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Any additional information or comments about your submission..."
                      />
                    </Grid>
                  </Grid>

                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleSubmit}
                    disabled={submitting || files.length === 0}
                    startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
                    sx={{
                      mt: 3,
                      py: 1.5,
                      bgcolor: SUCCESS,
                      '&:hover': { bgcolor: '#4a9c5a' },
                    }}
                  >
                    {submitting ? 'Submitting...' : 'Submit Task'}
                  </Button>
                </CardContent>
              </Card>
            )}
          </Grid>

          {/* Submission History */}
          <Grid item xs={12} md={5}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Submission History
                </Typography>

                {submissions.length === 0 ? (
                  <Alert severity="info">No submissions yet</Alert>
                ) : (
                  <List>
                    {submissions.map((submission, index) => (
                      <React.Fragment key={submission.submission_id}>
                        {index > 0 && <Divider />}
                        <ListItem alignItems="flex-start">
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <Typography variant="subtitle2" fontWeight={600}>
                                  Submission #{submissions.length - index}
                                </Typography>
                                <Chip
                                  label={submission.review_status.toUpperCase()}
                                  size="small"
                                  color={submission.review_status === 'reviewed' ? 'success' : 'warning'}
                                />
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Submitted: {new Date(submission.submitted_at).toLocaleString()}
                                </Typography>
                                {submission.review_status === 'reviewed' && (
                                  <>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Reviewed: {new Date(submission.reviewed_at).toLocaleString()}
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600} color={SUCCESS} mt={1}>
                                      Score: {submission.score}%
                                    </Typography>
                                  </>
                                )}
                                <Typography variant="caption" display="block" mt={1}>
                                  {submission.files?.length || 0} file(s) submitted
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </ThemeProvider>
  );
};

export default TechnicalTaskSubmission;
